import { DiContainer, Injectable } from '@jkhy/frontend-utils/di';
import { JhHttpResponseType } from '@jkhy/frontend-utils/http';
import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { ConfigurationService } from '@treasury/core/config';
import { TmHttpClient } from '@treasury/core/http';
import { LoggingService } from '@treasury/core/logging';
import { SessionStorageService, exists, getKeys } from '@treasury/utils';
import LaunchDarkly, { LDClient } from 'launchdarkly-js-client-sdk';
import { EntitlementDto, getEntitlementName } from './entitlement.dto';
import { normalizeFeatureName } from './feature-flag.helpers';
import {
    EntitlementFeatureFlag,
    Feature,
    FeatureChangedPayload,
    FeatureUser,
    LocalFeatureJson,
    TmFeatureFlag,
    TmFeatureType,
} from './feature-flag.types';

const STORAGE_PREFIX = 'TM_FF';

// TODO: consolidate this with BO feature flag service
@Injectable()
export class FeatureFlagService {
    constructor(
        private http: TmHttpClient,
        private storage: SessionStorageService,
        private config: ConfigurationService,
        private logger: LoggingService
    ) {}

    /** @deprecated Use DI instead of direct import. */
    public static async isEnabled(...flags: string[]) {
        const instance = await getServiceInstance();
        return instance.isEnabled(...(flags as Feature[]));
    }

    /** @deprecated Use DI instead of direct import. */
    public static async getFeatureFlags() {
        const instance = await getServiceInstance();

        return instance.getEntitlements();
    }

    public static getInstance() {
        return getServiceInstance();
    }

    private readonly readyStream = new ObservationSource<boolean>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly ready$ = this.readyStream.toObservable();

    private readonly featureChangedStream = new ObservationSource<FeatureChangedPayload>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly featureChanged$ = this.featureChangedStream.toObservable();

    private _features: Map<Lowercase<Feature>, TmFeatureFlag> = new Map();

    private initialized = false;

    private ldClient?: LDClient;

    private user?: FeatureUser;

    public get features() {
        return Array.from(this._features.values());
    }

    /**
     * Determine if one or more features are all enabled.
     * Consults local overrides first in dev builds.
     *
     * @param flagNames  A list of feature flag names to check.
     * @returns A promise containing `true` if—and only if—all features are enabled, otherwise `false`.
     */
    public async isEnabled(...flagNames: Feature[]) {
        await this.ready$.toPromise();

        return flagNames.every(name => {
            const locallyEnabled = this.getOverride(name);
            if (exists(locallyEnabled)) {
                return locallyEnabled;
            }

            const normalizedName = normalizeFeatureName(name);
            const feature = this._features.get(normalizedName);
            if (!feature) {
                this.logger.logWarning(
                    `Attempted to query status of feature ${name}, but it was not present in the feature flag registry.`
                );

                return false;
            }

            return feature.type === TmFeatureType.LaunchDarkly
                ? this.ldClient?.variation(name, false)
                : feature.enabled;
        });
    }

    public setOverride(featureName: Feature, enabled: boolean) {
        this.storage.set(`${STORAGE_PREFIX}.${normalizeFeatureName(featureName)}`, enabled);
        this.featureChangedStream.emit({
            featureName,
            enabled,
        });
    }

    public getOverride(featureName: Feature) {
        return this.storage.get<boolean>(`${STORAGE_PREFIX}.${normalizeFeatureName(featureName)}`);
    }

    public async init(fiId: string, user: FeatureUser, localFlagsPath?: string) {
        if (this.ready$.completed || this.initialized) {
            return;
        }
        this.user = user;

        let features: TmFeatureFlag[] = [];

        try {
            const promises = [
                this.getEntitlementFeatures(),
                this.createLdClient(fiId, user).catch(() => {
                    this.logger.logError(
                        'Could not initialize Launch Darkly  client. LD flags will be read as disabled.'
                    );
                    return [];
                }),
            ];

            if (localFlagsPath) {
                promises.push(this.getLocalFeatures(localFlagsPath));
            }

            features = (await Promise.all(promises)).flat();
        } catch (e) {
            this.readyStream.setError(new Error('Could not initialize feature flag service.'));
            this.readyStream.complete();
            throw e;
        }

        this.storeFeatureArray(features);
        this.initialized = true;
        this.readyStream.emit(true);
    }

    public async reset() {
        this._features.clear();
        await this.ldClient?.close();
        this.ldClient = undefined;
        this.initialized = false;
        this.readyStream.emit(false);
    }

    public async reInit(fiId?: string) {
        if (!this.user) {
            throw new Error(
                'Could not reinitialize feature flag service. There was no user data present.'
            );
        }

        await this.reset();
        await this.init(fiId || this.config.institutionId, this.user);
    }

    private async getLocalFeatures(path: string): Promise<TmFeatureFlag[]> {
        try {
            const json = await this.http.request<LocalFeatureJson>(path, {
                method: 'GET',
                responseType: JhHttpResponseType.Json,
                // disable base URL to request non-FI-scoped asset
                withoutBase: true,
            });

            return getKeys(json).map(k => {
                const value = json[k];
                const fiId = this.config.institutionId;
                const enabled = Array.isArray(value)
                    ? // normalize values to allow for all casing permutations
                      value.map(v => v.toLowerCase()).includes(fiId.toLowerCase())
                    : !!value;

                return {
                    type: TmFeatureType.Local,
                    name: k,
                    enabled,
                };
            });
        } catch {
            // fail back to empty array and warn if local feature flags are not available
            this.logger.logWarning(`Could not load local feature flags from ${path}.`);
            return [];
        }
    }

    /**
     * Get entitlements and convert them to a dictionary of `Feature` objects.
     * @returns
     */
    private async getEntitlementFeatures() {
        const entitlements = await this.getEntitlements();
        return entitlements.map(e => new EntitlementFeatureFlag(e));
    }

    /**
     * Get the underlying entitlement DTOs used to inform feature flag states.
     */
    private async getEntitlements() {
        const entitlements = await this.http.request<EntitlementDto[]>(`entitlements`, {
            method: 'GET',
            maxAgeInSeconds: 50000,
        });

        return entitlements.filter(e => getEntitlementName(e).toLowerCase().includes('feature'));
    }

    /**
     * Create an `LDClient` instance, assign it as a class member,
     * fetch its features and transform them into a `TmFeatureFlag` array.
     */
    private async createLdClient(fiId: string, user: FeatureUser) {
        const { app, ffToken } = this.config;
        const { companyId, userId, isAdmin, isSuperUser } = user;
        const ldClient = LaunchDarkly.initialize(
            ffToken,
            {
                kind: 'multi',
                user: {
                    kind: 'user',
                    key: userId,
                    isAdmin,
                    isSuperUser,
                },
                fi: {
                    kind: 'fi',
                    key: fiId.toLowerCase(),
                    companyId,
                },
                app: {
                    kind: 'app',
                    key: app,
                },
            },
            {
                sendEventsOnlyForVariation: true,
            }
        );

        try {
            await ldClient.waitForInitialization(20);
            this.ldClient = ldClient;
            return getLdFlags(ldClient);
        } catch (e) {
            this.logger.logError(
                'Launch Darkly client initialization failed.',
                e instanceof Error ? e : undefined
            );
            return Promise.resolve([]);
        }
    }

    private storeFeatureArray(features: TmFeatureFlag[]) {
        features.forEach(f => {
            this._features.set(normalizeFeatureName(f.name), f);
        });
    }
}

async function getLdFlags(ldClient: LDClient): Promise<TmFeatureFlag[]> {
    const flags = await ldClient.allFlags();

    return Object.keys(flags).map(featureName => {
        const value = flags[featureName];

        return {
            type: TmFeatureType.LaunchDarkly,
            name: featureName as Feature,
            enabled: !!value,
        };
    });
}

async function getServiceInstance() {
    return (await DiContainer.getInstance()).getAsync(FeatureFlagService);
}
